'use client';

import { faBellSlash } from '@fortawesome/free-regular-svg-icons';
import { useRouter } from 'next/navigation';
import { FC, useCallback, useEffect, useRef } from 'react';

import { Api } from '@/api';

import { EmptyResults } from '../EmptyResults';
import { Modal } from '../Modal';
import styles from './NotificationsModal.module.scss';
import { INotificationsModal } from './NotificationsModal.types';
import { NotificationRow } from './components/NotificationRow.component';

const { updateNotification } = Api.notifications;

export const NotificationsModal: FC<INotificationsModal> = ({
  isOpen,
  setIsOpen,
  notifications,
  hasNextPage,
  fetchNextPage,
  isFetching,
  refetch,
}) => {
  const { replace } = useRouter();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleClickRow = async (
    itemId: string,
    contentId: string,
    commentId?: string,
    parentCommentId?: string,
  ) => {
    try {
      await updateNotification({ id: itemId });
      refetch?.();

      localStorage?.setItem('commentId', '');
      if (commentId) localStorage.setItem('commentId', commentId);
      if (parentCommentId)
        localStorage.setItem('parentCommentId', parentCommentId);

      // Close modal first, then navigate after a small delay to avoid race conditions
      setIsOpen(false);

      // Use setTimeout to ensure modal closes before navigation
      setTimeout(() => {
        replace(`/feed/${contentId}`);
      }, 50);
    } catch (error) {
      console.error('Error handling row click:', error);
    }
  };

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || !hasNextPage || isFetching) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 40) {
      fetchNextPage?.();
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  useEffect(() => {
    if (isOpen) {
      const container = containerRef.current;
      if (container) {
        setTimeout(() => {
          container.addEventListener('scroll', handleScroll);
        }, 10);
      }
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        refetch?.();
      }, 200);
    }
  }, [isOpen]);

  const renderContent = () => {
    if (!notifications?.length) {
      return (
        <EmptyResults
          iconProps={{ icon: faBellSlash }}
          title="No hay notificaciones nuevas"
          body="Te notificaremos cuando haya contenido nuevo o alguien interactúe con tus comentarios"
        />
      );
    }

    return notifications.map(({ id, payload, wasRead, created }) => {
      const { message, thumbnailUrl, contentId, commentId, parentCommentId } =
        payload || {};

      return (
        <NotificationRow
          created={created}
          wasRead={wasRead}
          key={id}
          content={message}
          action={{ thumbnailUrl }}
          onClick={() =>
            handleClickRow(id, contentId, commentId, parentCommentId)
          }
        />
      );
    });
  };

  return (
    <Modal
      containerClassNames={styles.container}
      contentClassNames={styles.content}
      childrenClassNames={styles.children}
      title="Notificaciones"
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      renderAsFullPageOnMobile
    >
      <div ref={containerRef} className={styles.children}>
        {renderContent()}
      </div>
    </Modal>
  );
};
